import { useState, type FormEvent } from 'react'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { AudienceStep } from '@/components/campaigns/AudienceStep'
import { CampaignBasicsFields, type CampaignBasics } from '@/components/campaigns/CampaignBasicsFields'
import { LoadError } from '@/components/campaigns/LoadError'
import { RecipientsTable } from '@/components/campaigns/RecipientsTable'
import { ReviewStep } from '@/components/campaigns/ReviewStep'
import { StepsEditor } from '@/components/campaigns/StepsEditor'
import { DEFAULT_FILTERS } from '@/components/campaigns/campaignFormat'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { WizardSidebarStep } from '@/components/wizard/WizardSidebarStep'
import { getWizardStepStatus } from '@/components/wizard/step-status'
import { useAudienceConsentQuery, useCreateCampaign, useCrmCampaignQuery } from '@/queries'
import type { CrmCampaign } from '@/services/crmCampaignsService'

const STEP_KEYS = ['audience', 'recipients', 'messages', 'review'] as const

function BackLink() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  return (
    <Button asChild variant="link">
      <Link to={`/${lang}/campaigns`}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('campaigns.common.back_to_list')}
      </Link>
    </Button>
  )
}

/** Crear: lo mínimo para que exista el borrador. El resto del asistente trabaja ya sobre él. */
function NewCampaignForm() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const navigate = useNavigate()
  const create = useCreateCampaign()
  const [value, setValue] = useState<CampaignBasics>({
    nombre: '',
    audiencia_tipo: 'filtros',
    excluir_agentes_excluidos: true,
    parar_si_responde: true,
  })
  const [nameError, setNameError] = useState<string>()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!value.nombre.trim()) {
      setNameError(t('campaigns.editor.name_required'))
      document.getElementById('campaign-name')?.focus()
      return
    }
    setNameError(undefined)
    create.mutate(
      { ...value, nombre: value.nombre.trim(), filtros: DEFAULT_FILTERS },
      { onSuccess: (campaign) => navigate(`/${lang}/campaigns/${campaign.id}/edit`, { replace: true }) },
    )
  }

  return (
    <div className="container mx-auto max-w-3xl space-y-8 p-4 md:p-6">
      <div className="space-y-2">
        <BackLink />
        <PageHeader title={t('campaigns.editor.new_title')} description={t('campaigns.editor.new_description')} />
      </div>
      <form className="space-y-4" onSubmit={submit} noValidate>
        <CampaignBasicsFields value={value} nameError={nameError} onChange={setValue} />
        {create.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{create.error.message}</AlertDescription>
          </Alert>
        ) : null}
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t('campaigns.editor.create')}
        </Button>
      </form>
    </div>
  )
}

function CampaignWizard({ campaign }: { campaign: CrmCampaign }) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState(0)
  const [visited, setVisited] = useState<Set<number>>(new Set([0]))
  const consent = useAudienceConsentQuery(campaign.id)

  const goTo = (step: number) => {
    setCurrent(step)
    setVisited((prev) => new Set(prev).add(step))
  }

  const hasAudience = (consent.data?.total ?? 0) > 0 || campaign.destinatarios > 0
  const isStepValid = (step: number) => {
    if (step === 0 || step === 1) return hasAudience
    if (step === 2) return campaign.steps.length > 0
    return true
  }

  return (
    <div className="container mx-auto space-y-8 p-4 md:p-6">
      <div className="space-y-2">
        <BackLink />
        <PageHeader title={campaign.nombre} description={t('campaigns.editor.edit_description')} />
      </div>

      <div className="grid gap-8 md:grid-cols-[14rem_1fr]">
        <nav aria-label={t('campaigns.editor.steps_label')}>
          {STEP_KEYS.map((key, index) => (
            <WizardSidebarStep
              key={key}
              title={t(`campaigns.editor.step.${key}`)}
              stepLabel={t('campaigns.editor.step_number', { number: index + 1 })}
              status={getWizardStepStatus({
                itemStep: index,
                currentStep: current,
                visitedSteps: visited,
                isStepValid,
              })}
              onClick={() => goTo(index)}
            />
          ))}
        </nav>

        <div className="min-w-0 space-y-8">
          {current === 0 ? <AudienceStep campaign={campaign} /> : null}
          {current === 1 ? <RecipientsTable campaignId={campaign.id} mode="review" /> : null}
          {/* Se queda montado al salir de él: lo escrito y sin guardar no se pierde por mirar otro paso. */}
          {visited.has(2) ? (
            <div className={current === 2 ? undefined : 'hidden'}>
              <StepsEditor campaign={campaign} onSaved={() => goTo(3)} />
            </div>
          ) : null}
          {current === 3 ? <ReviewStep campaign={campaign} /> : null}

          <div className="flex justify-between gap-2">
            <Button type="button" variant="ghost" disabled={current === 0} onClick={() => goTo(current - 1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('campaigns.common.previous')}
            </Button>
            {current < STEP_KEYS.length - 1 ? (
              <Button type="button" variant="outline" onClick={() => goTo(current + 1)}>
                {t('campaigns.common.next')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function ExistingCampaign({ id }: { id: string }) {
  const { lang = 'es' } = useParams()
  const campaign = useCrmCampaignQuery(id)

  if (campaign.isPending) {
    return (
      <div className="container mx-auto space-y-4 p-4 md:p-6" data-testid="editor-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (campaign.isError) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <LoadError error={campaign.error} onRetry={() => void campaign.refetch()} />
      </div>
    )
  }
  // Solo se edita un borrador (R3.1). Una vez lanzada, lo que hay es su panel.
  if (campaign.data.estado !== 'borrador') return <Navigate to={`/${lang}/campaigns/${id}`} replace />
  return <CampaignWizard campaign={campaign.data} />
}

export function CampaignEditorPage() {
  const { id } = useParams()
  return id ? <ExistingCampaign id={id} /> : <NewCampaignForm />
}
